import { unavailableHatch } from "./availability-bands";

interface ScheduleBarLegendProps {
  label?: string;
  /** Show the technician/machine AM/PM availability swatch + OT footnote (WOR-2187). */
  showAvailability?: boolean;
}

interface BarLegendConfig {
  key: string;
  label: string;
  dotClassName: string;
}

const LEGEND_CONTAINER_CLASS_NAME =
  "aero:flex aero:items-center aero:gap-4 aero:text-xs aero:text-muted-foreground aero:flex-wrap";

const BAR_LEGEND_CONFIGS: BarLegendConfig[] = [
  {
    key: "red",
    label: "Unavailable",
    dotClassName: "aero:bg-red-500"
  },
  {
    key: "green",
    label: "Available",
    dotClassName: "aero:bg-green-500"
  }
];

// Calendar day-background swatches (square chips) — match the grid tokens (WOR-2187).
const DAY_BG_LEGEND_CONFIGS: BarLegendConfig[] = [
  {
    key: "weekend",
    label: "Weekend",
    dotClassName: "aero:bg-gray-200 aero:border aero:border-gray-300"
  },
  {
    key: "holiday",
    label: "Public holiday",
    dotClassName: "aero:bg-yellow-200 aero:border aero:border-amber-300"
  }
];

// Shares the hatch geometry with AvailabilityBands (via unavailableHatch) so the swatch can't
// drift from the cell; alpha is boosted here for legibility at the 10px swatch size.
const UNAVAILABLE_HATCH = unavailableHatch(0.45, 0.15);

export default function ScheduleBarLegend({
  label = "Bar Legend:",
  showAvailability = false
}: ScheduleBarLegendProps) {
  return (
    <div className={LEGEND_CONTAINER_CLASS_NAME}>
      <span>{label}</span>
      {BAR_LEGEND_CONFIGS.map(({ key, label: itemLabel, dotClassName }) => (
        <div key={key} className="aero:flex aero:items-center aero:gap-1.5">
          <div
            className={`aero:w-2.5 aero:h-2.5 aero:rounded-full aero:flex-shrink-0 ${dotClassName}`}
          />
          <span className="aero:text-foreground">{itemLabel}</span>
        </div>
      ))}

      <span className="aero:text-border">|</span>

      {DAY_BG_LEGEND_CONFIGS.map(({ key, label: itemLabel, dotClassName }) => (
        <div key={key} className="aero:flex aero:items-center aero:gap-1.5">
          <div
            className={`aero:w-2.5 aero:h-2.5 aero:rounded-[2px] aero:flex-shrink-0 ${dotClassName}`}
          />
          <span className="aero:text-foreground">{itemLabel}</span>
        </div>
      ))}

      {showAvailability && (
        <>
          <span className="aero:text-border">|</span>
          <div className="aero:flex aero:items-center aero:gap-1.5">
            <div
              className="aero:w-2.5 aero:h-2.5 aero:rounded-[2px] aero:flex-shrink-0 aero:border aero:border-red-300"
              style={{ backgroundImage: UNAVAILABLE_HATCH }}
            />
            <span className="aero:text-foreground">AM / PM unavailable</span>
          </div>
          <span className="aero:italic">
            OT (22:00–06:00) is unavailable by default
          </span>
        </>
      )}
    </div>
  );
}
